import { useContext, useState, useEffect } from 'react';
import { Search, Eye, History, Calendar, User, Filter, List, UserCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import AuthContext from '../../../../../../context/AuthContext';
import { useToast } from '../../../../../../context/ToastContext';
import Pagination from '../../../../../../components/Pagination';
import auditHistoryService from '../../../../../../services/audit_services/auditHistoryService';

export default function AuditSectionHistoryList() {
  const { authTokens, hasRole } = useContext(AuthContext);
  const { showError } = useToast();

  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [actionTypeFilter, setActionTypeFilter] = useState('');
  const [ordering, setOrdering] = useState('newest');
  const [viewMode, setViewMode] = useState('all'); // 'all' or 'my' - for AuditManager toggle
  
  const [pagination, setPagination] = useState({
    count: 0,
    page: 1,
    page_size: 20,
    total_pages: 0
  });

  // Determine if user is AuditManager or AuditUser
  const isAuditManager = hasRole('AuditManager');

  useEffect(() => {
    fetchHistory();
  }, [pagination.page, pagination.page_size, actionTypeFilter, ordering, viewMode]);

  // Trigger search when searchTerm changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setPagination(prev => ({ ...prev, page: 1 }));
      fetchHistory();
    }, 500); // Debounce search

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const fetchHistory = async () => {
    setLoading(true);
    
    try {
      const params = {
        search: searchTerm,
        action_type: actionTypeFilter,
        ordering: ordering,
        page: pagination.page,
        page_size: pagination.page_size
      };

      // Use appropriate service method based on role and view mode
      // AuditUser always sees "my" view, AuditManager can toggle
      const shouldUseMyView = !isAuditManager || viewMode === 'my';
      
      const result = shouldUseMyView
        ? await auditHistoryService.listMyAuditSectionHistory(authTokens.access, params)
        : await auditHistoryService.listAuditSectionHistory(authTokens.access, params);
      
      if (result.success) {
        setHistory(result.data.results || []);
        setPagination(prev => ({
          ...prev,
          count: result.data.count || 0,
          total_pages: result.data.total_pages || 0
        }));
      } else {
        showError(result.error || "Erreur lors du chargement de l'historique des sections d'audit");
        setHistory([]);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching audit section history:', error);
      showError("Erreur lors du chargement de l'historique des sections d'audit");
      setLoading(false);
    }
  };

  const getActionBadge = (actionType) => {
    const actionConfig = {
      create: { bg: 'bg-green-100', text: 'text-green-800', label: 'Créé' },
      update: { bg: 'bg-green-100', text: 'text-green-800', label: 'Mis à jour' },
      delete: { bg: 'bg-red-100', text: 'text-red-800', label: 'Supprimé' }
    };

    const config = actionConfig[actionType] || { bg: 'bg-gray-100', text: 'text-gray-800', label: actionType };
    
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement de l'historique des sections d'audit...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {viewMode === 'my' ? "Mon historique de sections d'audit" : "Historique des sections d'audit"}
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            {viewMode === 'my'
              ? "Consultez les modifications et actions sur les sections d'audit des plans auxquels vous participez" 
              : "Consultez toutes les modifications et actions sur les sections d'audit"}
          </p>
        </div>
        
        {/* Toggle button for AuditManager */}
        {isAuditManager && (
          <div className="flex gap-2">
            <button
              onClick={() => {
                setViewMode('all');
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              className={`inline-flex items-center px-4 py-2 rounded-lg transition-colors ${
                viewMode === 'all'
                  ? 'bg-green-600 text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              <List className="h-4 w-4 mr-2" />
              Toutes les sections
            </button>
            <button
              onClick={() => {
                setViewMode('my');
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              className={`inline-flex items-center px-4 py-2 rounded-lg transition-colors ${
                viewMode === 'my'
                  ? 'bg-green-600 text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              <UserCheck className="h-4 w-4 mr-2" />
              Mes sections
            </button>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher dans l'historique..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
          />
        </div>

        {/* Filter Row */}
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Filter className="inline h-4 w-4 mr-1" />
              Type d'action
            </label>
            <select
              value={actionTypeFilter}
              onChange={(e) => setActionTypeFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="">Toutes les actions</option>
              <option value="create">Créé</option>
              <option value="update">Mis à jour</option>
              <option value="delete">Supprimé</option>
            </select>
          </div>

          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Trier par
            </label>
            <select
              value={ordering}
              onChange={(e) => setOrdering(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="newest">Plus récents</option>
              <option value="oldest">Plus anciens</option>
            </select>
          </div>
        </div>
      </div>

      {/* History List */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {history.length === 0 ? (
          <div className="text-center py-12">
            <History className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Aucun enregistrement trouvé</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Section
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Plan
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Effectué par
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {history.map((record) => (
                  <tr key={record.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getActionBadge(record.action_type)}
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm font-medium text-gray-900">{record.audit_section_title}</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-gray-600">{record.audit_plan_title}</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-gray-600">{record.description}</p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-600">
                        <User className="h-4 w-4 mr-1 text-gray-400" />
                        {record.done_by_name || 'Inconnu'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-600">
                        <Calendar className="h-4 w-4 mr-1 text-gray-400" />
                        {formatDate(record.action_date)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <Link
                        to={`/dashboard/audit/history/sections/${record.id}?view=${viewMode}`}
                        className="text-green-600 hover:text-green-900"
                        title="Voir les détails"
                      >
                        <Eye className="h-4 w-4 inline" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {history.length > 0 && (
        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.total_pages}
          onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
          pageSize={pagination.page_size}
          onPageSizeChange={(size) => setPagination(prev => ({ ...prev, page_size: size, page: 1 }))}
          totalItems={pagination.count}
        />
      )}
    </div>
  );
}
